import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';

import { toast } from '@dashboard/context/ToastContext';
import { useUpdateMerchant } from '@dashboard/hooks/queries/useAdminMutations';
import { adminAiUsageKey } from '@dashboard/lib/queryKeys';

import { Label, TextInput } from '@dashboard/components/Form';
import { Button } from '@dashboard/components/ui/button';

import { formatMicros } from './aiUsagePeriod';

type Props = { sellerId: string; overrideUsd: number | null; planBudgetMicros: number };

type BudgetUpdate = { sellerId: string; data: { aiMonthlyBudgetUsd: number | null } };

const isValidBudget = (value: string) =>
  value.trim() !== '' && Number.isFinite(Number(value)) && Number(value) >= 0;

const AiBudgetForm = ({ sellerId, overrideUsd, planBudgetMicros }: Props) => {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(overrideUsd == null ? '' : String(overrideUsd));
  const [error, setError] = useState<string | null>(null);

  const updateMerchant = useUpdateMerchant({
    onSuccess: (_data: unknown, { data }: BudgetUpdate) => {
      queryClient.invalidateQueries({ queryKey: adminAiUsageKey });
      toast({ title: 'AI budget saved' });
      if (data.aiMonthlyBudgetUsd === null) setValue('');
    },
  });

  const save = async (aiMonthlyBudgetUsd: number | null) => {
    try {
      await updateMerchant.mutateAsync({ sellerId, data: { aiMonthlyBudgetUsd } });
    } catch (saveError: any) {
      toast({
        title: "Couldn't save the AI budget",
        description: saveError?.message,
        type: 'error',
      });
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!isValidBudget(value)) {
      setError('Enter an amount of 0 or more');
      return;
    }
    save(Number(value));
  };

  return (
    <form noValidate onSubmit={handleSubmit} className="space-y-1">
      <Label htmlFor="aiMonthlyBudgetUsd">Monthly AI budget (USD)</Label>
      <div className="flex items-center gap-x-2">
        <div className="w-36">
          <TextInput
            id="aiMonthlyBudgetUsd"
            name="aiMonthlyBudgetUsd"
            type="number"
            min="0"
            step="1"
            placeholder="Plan budget"
            value={value}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setValue(event.target.value);
              setError(null);
            }}
            aria-invalid={error ? true : undefined}
            aria-describedby="aiMonthlyBudgetUsd-help"
          />
        </div>
        <Button type="submit" disabled={updateMerchant.isPending}>
          Save budget
        </Button>
        {overrideUsd != null && (
          <Button
            type="button"
            variant="ghost"
            disabled={updateMerchant.isPending}
            onClick={() => save(null)}
          >
            Use plan budget
          </Button>
        )}
      </div>
      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      <p id="aiMonthlyBudgetUsd-help" className="text-sm text-gray-500 dark:text-muted-foreground">
        {`Plan budget: ${formatMicros(planBudgetMicros)}`}
      </p>
    </form>
  );
};

export default AiBudgetForm;
